import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { formatCount } from '../components/MegaMenu';
import SEOHead from '../components/common/SEOHead';
import './LandingPage.css';

// -- Persona definitions --
const personas = [
  {
    icon: '\u2699\uFE0F',
    title: 'Industry',
    tagline: 'Trade routes, mining locations, refinery yields, and crafting recipes.',
    link: '/industry/mining',
    linkText: 'Explore',
    staticStat: 'Trading \u00b7 Mining \u00b7 Refining \u00b7 Crafting',
  },
  {
    icon: '\uD83D\uDD28',
    title: 'Gear Hunting & Crafting',
    tagline: 'Find gear at shops, in loot, or craft better versions with blueprints.',
    link: '/items',
    linkText: 'Explore',
    statKey: 'items',
    statLabel: 'items in database',
  },
  {
    icon: '\uD83D\uDE80',
    title: 'Pilot',
    tagline: 'Every spec and loadout slot documented.',
    link: '/ship-matrix',
    linkText: 'Explore',
    statKey: 'ships',
    statLabel: 'ships cataloged',
  },
  {
    icon: '\uD83D\uDC65',
    title: 'Crew Finder',
    tagline: 'Find squadmates for any mission type.',
    link: '/crewlink',
    linkText: 'Explore',
    staticStat: 'Combat \u00b7 Mining \u00b7 Salvage \u00b7 Exploration \u00b7 Medical SOS',
  },
  {
    icon: '\uD83C\uDFDB\uFE0F',
    title: 'Org Leader',
    tagline: 'Fleet, cargo, events, operations, and DKP.',
    link: '/features/orgs',
    linkText: 'Explore',
    staticStat: '20+ management tools',
  },
];

const LandingPage = () => {
  const [stats, setStats] = useState({
    items: '2,500+',
    commodities: '120+',
    locations: '100+',
    ships: '400+',
  });
  const [liveCounts, setLiveCounts] = useState({});

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const [
          { count: itemCount },
          { count: commodityCount },
          { count: locationCount },
          { count: shipCount },
        ] = await Promise.all([
          supabase.from('items').select('*', { count: 'exact', head: true }),
          supabase.from('items').select('*', { count: 'exact', head: true }).eq('category', 'Commodities'),
          supabase.from('merchants').select('*', { count: 'exact', head: true }),
          supabase.from('ships').select('*', { count: 'exact', head: true }),
        ]);
        if (!cancelled) {
          setStats({
            items: itemCount ? `${Math.floor(itemCount / 100) * 100}+` : '2,500+',
            commodities: commodityCount ? `${commodityCount}+` : '120+',
            locations: locationCount ? `${locationCount}+` : '100+',
            ships: shipCount ? `${shipCount}+` : '400+',
          });
          setLiveCounts({
            items: itemCount,
            commodities: commodityCount,
            ships: shipCount,
            locations: locationCount,
          });
        }
      } catch { /* keep defaults */ }
    };
    fetchStats();
    return () => { cancelled = true; };
  }, []);

  return (
  <div className="landing-page">
    <SEOHead
      title={null}
      description="All game data is free, forever. Items, ships, commodity prices, locations, and routes — browse everything without an account. The Orbital Ledger is a community-powered Star Citizen companion."
      path="/"
    />
    {/* -- Hero -- */}
    <section className="landing-hero">
      <div className="landing-hero-content">
        <h1 className="landing-hero-title">
          All Game Data.<br />
          <span className="landing-hero-accent">Free. Forever.</span>
        </h1>
        <p className="landing-hero-subtitle">
          Items, ships, minerals, prices, locations, routes &mdash;
          browse everything without an account.
          <br />
          Built by citizens, for citizens.
        </p>
        <div className="landing-hero-actions">
          <Link to="/items" className="landing-btn landing-btn-primary">
            Explore the Database
          </Link>
          <Link to="/register" className="landing-btn landing-btn-secondary">
            Create Free Account
          </Link>
        </div>
        <p className="landing-hero-note">
          Game data is free forever. Premium services for personal tracking and org management.
        </p>
        <div style={{ display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap', marginTop: 8 }}>
          <Link to="/org/demo" style={{ color: '#00D1D1', fontSize: '0.88rem', fontWeight: 600, textDecoration: 'none' }}>
            See a demo org in action &rarr;
          </Link>
          <Link to="/demo/my-ledger" style={{ color: '#FFD447', fontSize: '0.88rem', fontWeight: 600, textDecoration: 'none' }}>
            Preview My Ledger &rarr;
          </Link>
        </div>
      </div>
    </section>

    {/* -- Citizen Selector -- */}
    <section className="landing-personas">
      <div className="landing-personas-header">
        <h2 className="landing-section-title">
          What kind of <span className="landing-hero-accent">citizen</span> are you?
        </h2>
        <p className="landing-section-subtitle">
          Choose your path. We'll show you what matters most.
        </p>
      </div>
      <div className="landing-personas-grid">
        {personas.map((p) => {
          const count = p.statKey ? formatCount(liveCounts[p.statKey]) : null;
          return (
            <Link key={p.title} to={p.link} className="landing-persona-card">
              <div className="landing-persona-icon">{p.icon}</div>
              <h3 className="landing-persona-title">{p.title}</h3>
              <p className="landing-persona-tagline">{p.tagline}</p>
              {(count || p.staticStat) && (
                <div className="landing-persona-stat">
                  {count ? (
                    <>
                      <span className="landing-persona-stat-number">{count}</span>
                      <span className="landing-persona-stat-label">{p.statLabel}</span>
                    </>
                  ) : (
                    <span className="landing-persona-stat-label">{p.staticStat}</span>
                  )}
                </div>
              )}
              <span className="landing-persona-cta">{p.linkText} &rarr;</span>
            </Link>
          );
        })}
      </div>
      <div className="landing-coming-soon-bar">
        <span className="landing-coming-soon-label">NOW LIVE</span>
        <span className="landing-coming-soon-items">
          Mining &middot; Refining &middot; Equipment &middot; Route Planner
        </span>
      </div>
    </section>

    {/* -- Community Stats -- */}
    <section className="landing-community">
      <div className="landing-community-card">
        <h2 className="landing-section-title">Powered by the Community</h2>
        <p className="landing-section-subtitle">
          Every price submission, every item report, every location confirmation
          makes the data better for everyone.
        </p>
        <div className="landing-stats-row">
          <div className="landing-stat">
            <div className="landing-stat-number">{stats.items}</div>
            <div className="landing-stat-label">Items Tracked</div>
          </div>
          <div className="landing-stat">
            <div className="landing-stat-number">{stats.commodities}</div>
            <div className="landing-stat-label">Commodities</div>
          </div>
          <div className="landing-stat">
            <div className="landing-stat-number">{stats.locations}</div>
            <div className="landing-stat-label">Trade Locations</div>
          </div>
          <div className="landing-stat">
            <div className="landing-stat-number">Free</div>
            <div className="landing-stat-label">Game Data</div>
          </div>
        </div>
      </div>
    </section>

    {/* -- Personal Ledger CTA -- */}
    <section className="landing-premium">
      <div className="landing-premium-card">
        <h2 className="landing-premium-title">Your Personal Ledger</h2>
        <p className="landing-premium-desc">
          Track your inventory, save loadouts, set goals, and sync your
          career data across devices. Or level up your org with shared
          fleet management, cargo tracking, and analytics.
        </p>
        <p className="landing-premium-desc" style={{ fontSize: '0.9rem', opacity: 0.8, marginTop: '0.5rem' }}>
          All game data is free forever. Your Ledger covers cloud storage
          for your personal data &mdash; that's it.
        </p>
        <div className="landing-premium-pricing">
          <span className="landing-premium-trial">14-day free trial &middot; no credit card required</span>
        </div>
        <Link to="/purchase" className="landing-btn landing-btn-gold">
          Start Free Trial
        </Link>
        <p style={{ marginTop: 16, marginBottom: 0 }}>
          <Link to="/org/demo" style={{ color: '#00d1d1', fontSize: '0.88rem', fontWeight: 600, textDecoration: 'none' }}>
            See what an org looks like &rarr;
          </Link>
        </p>
      </div>
    </section>

    {/* -- FAQ Link -- */}
    <section style={{ textAlign: 'center', padding: '0 20px 24px' }}>
      <p style={{ color: '#8899aa', fontSize: '0.9rem' }}>
        Have questions? Check out our <Link to="/faq" style={{ color: '#00d1d1' }}>FAQ</Link>,
        visit <Link to="/support" style={{ color: '#00d1d1' }}>Support</Link>, or
        read the <Link to="/about" style={{ color: '#00d1d1' }}>About</Link> page.
      </p>
    </section>

    {/* -- CIG Disclaimer -- */}
    <section className="landing-disclaimer">
      <p>
        The Orbital Ledger is an unofficial fan-made project and is not affiliated with,
        endorsed by, or sponsored by Cloud Imperium Games Corporation or Roberts Space
        Industries. Star Citizen is a registered trademark of Cloud Imperium Games
        Corporation.
      </p>
    </section>
  </div>
  );
};

export default LandingPage;
